import ArrowDownIcon from "../../assets/arrowDown.svg";
import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
  type FocusEvent,
} from "react";

interface DropDownProps {
  label: string;
  options: string[];
  selectedOption: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  isEnabled?: boolean;
}

export const DropDown = ({
  label,
  options,
  selectedOption,
  onSelect,
  placeholder = "Select an option",
  isEnabled = false,
}: DropDownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<"above" | "below">(
    "below"
  );

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < 150 && spaceAbove > spaceBelow) {
        setDropdownPosition("above");
      } else {
        setDropdownPosition("below");
      }
    }
  }, [isOpen]);

  const handleSelect = (option: string) => {
    onSelect(option);
    setSearchTerm("");
    setIsOpen(false);
    setFocusedOptionIndex(-1);
  };

  const handleInputMouseDown = () => {
    if (!isEnabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
    setFocusedOptionIndex(-1);
  };

  const handleInputFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false);
      setFocusedOptionIndex(-1);
    }
  };

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (focusedOptionIndex >= 0 && optionRefs.current[focusedOptionIndex]) {
      optionRefs.current[focusedOptionIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [focusedOptionIndex]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) {
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
      );
    } else if (e.key === "Enter" && focusedOptionIndex >= 0) {
      handleSelect(filteredOptions[focusedOptionIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setFocusedOptionIndex(-1);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block mb-2 text-sm font-medium text-gray-900">
        {label}
      </label>
      <div className="relative w-full">
        <input
          type="text"
          onMouseDown={handleInputMouseDown}
          onChange={isEnabled ? handleInputChange : undefined}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-z-dark-moss-green focus:border-z-dark-moss-green block w-full p-2.5 pr-10 ${isEnabled ? "" : "cursor-pointer"
            }`}
          placeholder={selectedOption || placeholder}
          value={isEnabled ? searchTerm : selectedOption}
          readOnly={!isEnabled}
        />
        <img
          src={ArrowDownIcon}
          alt=""
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"
            }`}
        />
      </div>

      {isOpen && (
        <div
          className={`absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto no-scrollbar ${dropdownPosition === "above" ? "bottom-full mb-1" : "mt-1"
            }`}
          tabIndex={-1}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option}
                ref={(el) => (optionRefs.current[index] = el)}
                onMouseDown={() => handleSelect(option)}
                className={`px-4 py-2 cursor-pointer hover:bg-z-dark-moss-green ${option === selectedOption
                    ? "bg-z-dark-moss-green text-white"
                    : ""
                  } ${index === focusedOptionIndex ? "bg-z-dark-moss-green" : ""}`}
              >
                {option}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};
